import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, type Header } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, GripVertical } from "lucide-react";
import { type CSSProperties, type ReactNode } from "react";

import { Text } from "@/components/ui";
import { TableHeaderContextMenu } from "@/components/ui/shadcn/DataTable/TableHeaderContextMenu";
import { cn } from "@/lib/utils/cn";

export type MattersStyleColumnMeta = {
  cellClassName?: string;
  /**
   * Align header label and sort control with right-aligned numeric cells.
   * Default is start (left in LTR).
   */
  headerAlign?: "start" | "end";
};

export function MattersStyleHeaderLabelAndSort({
  header,
  labelEl,
  labelAlign = "start",
}: {
  header: Header<unknown, unknown>;
  labelEl: ReactNode;
  labelAlign?: "start" | "end";
}) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const canHide = header.column.getCanHide();

  const labelContent = (
    <Text
      as="span"
      variant={canHide ? "subHeading" : "body"}
      className={cn(!canHide && "font-semibold")}
    >
      {labelEl}
    </Text>
  );

  if (!canSort) {
    return (
      <span
        className={cn(
          "inline-flex min-w-0 items-center gap-1",
          labelAlign === "end" && "justify-end text-right",
        )}
      >
        {labelContent}
      </span>
    );
  }

  const Icon =
    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;

  return (
    <button
      type="button"
      className={cn(
        "text-foreground hover:bg-muted/50 inline-flex max-w-full min-w-0 cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5",
        labelAlign === "end" ? "text-right" : "text-left",
      )}
      aria-label={`Sort by ${String(header.column.id)}`}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={header.column.getToggleSortingHandler()}
    >
      <span className="min-w-0">{labelContent}</span>
      <Icon
        className={cn(
          "text-muted-foreground h-3.5 w-3.5 shrink-0",
          sorted ? "text-foreground" : "opacity-55",
        )}
        aria-hidden
      />
    </button>
  );
}

/** Matches My Matters dashboard: reorder grip left of label on every column. */
export function MattersStyleDraggableHeader<T>({
  header,
  sticky = false,
}: {
  header: Header<T, unknown>;
  sticky?: boolean;
}) {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });

  const style: CSSProperties = {
    opacity: isDragging ? 0.65 : 1,
    position: sticky ? "sticky" : "relative",
    top: sticky ? 0 : undefined,
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? "none" : "transform 200ms ease-in-out",
    minWidth: header.column.getSize(),
    zIndex: isDragging ? 30 : sticky ? 20 : 0,
  };

  const labelEl = flexRender(
    header.column.columnDef.header,
    header.getContext(),
  );

  const meta = header.column.columnDef.meta as
    | MattersStyleColumnMeta
    | undefined;
  const headerAlignEnd = meta?.headerAlign === "end";

  return (
    <TableHeaderContextMenu
      header={header}
      trigger={
        <th
          ref={setNodeRef}
          style={style}
          className={cn(
            "text-foreground px-4 py-3 align-middle whitespace-nowrap",
            headerAlignEnd ? "text-right" : "text-left",
            sticky && "bg-muted",
          )}
        >
          {header.isPlaceholder ? null : (
            <span
              className={cn(
                "inline-flex max-w-full items-center gap-1",
                headerAlignEnd && "w-full justify-end",
              )}
            >
              <button
                type="button"
                aria-label={`Reorder ${String(header.column.id)} column`}
                className="text-muted-foreground inline-flex shrink-0 cursor-grab items-center rounded-sm active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </button>
              <MattersStyleHeaderLabelAndSort
                header={header as Header<unknown, unknown>}
                labelAlign={headerAlignEnd ? "end" : "start"}
                labelEl={labelEl}
              />
            </span>
          )}
        </th>
      }
    />
  );
}
